import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Store, Search, Briefcase, Clock, CheckCircle2 } from 'lucide-react';

const STATUS_STYLES = {
  open: 'bg-green-100 text-green-700',
  applications_closed: 'bg-amber-100 text-amber-700',
  assigned: 'bg-blue-100 text-blue-700',
  completed: 'bg-gray-100 text-gray-600',
};

const APP_STATUS_STYLES = {
  submitted: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const StatusBadge = ({ status }) => (
  <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
    {String(status || '').replace(/_/g, ' ')}
  </span>
);

const PartnerMarketplace = () => {
  const { getAuthHeaders, API_URL } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [verticalFilter, setVerticalFilter] = useState('all');
  const [skillFilter, setSkillFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [projRes, appRes] = await Promise.all([
        axios.get(`${API_URL}/api/marketplace/projects`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/marketplace/applications`, { headers: getAuthHeaders() }),
      ]);
      setProjects(projRes.data);
      setApplications(appRes.data);
    } catch (error) {
      console.error('Failed to load marketplace:', error);
      toast.error('Failed to load marketplace');
    } finally {
      setLoading(false);
    }
  };

  const verticals = [...new Set(projects.map((p) => p.vertical).filter(Boolean))];
  const allSkills = [...new Set(projects.flatMap((p) => p.skills || []))];

  const filteredProjects = projects.filter((p) => {
    const matchesSearch =
      p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.customer?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesVertical = verticalFilter === 'all' || p.vertical === verticalFilter;
    const matchesSkill = skillFilter === 'all' || (p.skills || []).includes(skillFilter);
    return matchesSearch && matchesVertical && matchesSkill;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2">
            <Store className="w-6 h-6 text-[#ef785b]" /> Marketplace
          </h2>
          <p className="text-[#64748B] text-sm mt-1">Browse open Expedify projects and apply to deliver them.</p>
        </div>

        <Tabs defaultValue="open" className="w-full">
          <TabsList>
            <TabsTrigger value="open">Open Projects</TabsTrigger>
            <TabsTrigger value="mine">My Applications ({applications.length})</TabsTrigger>
          </TabsList>

          {/* Open projects */}
          <TabsContent value="open" className="space-y-4 mt-4">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={verticalFilter} onValueChange={setVerticalFilter}>
                <SelectTrigger className="w-full md:w-48"><SelectValue placeholder="Vertical" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All verticals</SelectItem>
                  {verticals.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={skillFilter} onValueChange={setSkillFilter}>
                <SelectTrigger className="w-full md:w-48"><SelectValue placeholder="Skill" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All skills</SelectItem>
                  {allSkills.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="text-center py-12 text-[#64748B]">Loading projects...</div>
            ) : filteredProjects.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-[#64748B]">
                <Briefcase className="w-10 h-10 mx-auto mb-3 text-[#CBD5E1]" />
                No open projects match your filters.
              </CardContent></Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredProjects.map((p) => (
                  <Card
                    key={p.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate(`/marketplace/${p.id}`)}
                  >
                    <CardContent className="p-5 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold text-[#1A1A1A] leading-tight">{p.title}</h3>
                        <StatusBadge status={p.status} />
                      </div>
                      {p.customer && <p className="text-xs text-[#64748B]">{p.customer}{p.vertical ? ` · ${p.vertical}` : ''}</p>}
                      <p className="text-sm text-[#475569] line-clamp-3">{p.description}</p>
                      {(p.skills || []).length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {p.skills.slice(0, 4).map((s) => (
                            <Badge key={s} variant="secondary" className="text-[10px] font-normal">{s}</Badge>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-1 border-t border-[#F0F0EE] mt-2">
                        <span className="text-xs text-[#64748B] flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {p.timeline || 'Flexible'}
                        </span>
                        {p.has_applied
                          ? <span className="text-xs text-green-600 font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Applied</span>
                          : <span className="text-xs text-[#ef785b] font-medium">View &amp; apply →</span>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My applications */}
          <TabsContent value="mine" className="space-y-3 mt-4">
            {applications.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-[#64748B]">
                You haven't applied to any projects yet.
              </CardContent></Card>
            ) : (
              applications.map((a) => (
                <Card
                  key={a.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/marketplace/${a.project_id}`)}
                >
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-[#1A1A1A] truncate">{a.project_title}</p>
                      <p className="text-xs text-[#64748B]">
                        Applied {a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}
                        {a.proposed_timeline ? ` · Proposed: ${a.proposed_timeline}` : ''}
                      </p>
                      {a.decision_reason && <p className="text-xs text-[#64748B] mt-1 italic">{a.decision_reason}</p>}
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize shrink-0 ${APP_STATUS_STYLES[a.status] || 'bg-gray-100 text-gray-600'}`}>
                      {a.status}
                    </span>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default PartnerMarketplace;
